import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const situationTargetingArticle: BlogArticle = {
  key: BlogArticleKey.SITUATION_TARGETING,
  slug: BlogSlugs.SITUATION_TARGETING,
  categoryId: "04",
  date: "2026-07-26",
  readTime: 7,
  locales: {
    fr: {
      title: "Cibler une situation plutôt qu'un profil pour être lu",
      excerpt:
        "Un titre de poste ne dit pas si quelqu'un a besoin de toi aujourd'hui. Voici comment cibler la situation plutôt que le profil, et obtenir des réponses.",
      description:
        "En prospection B2B, un titre de poste ne dit pas si quelqu'un a besoin de toi. Cibler la situation plutôt que le profil.",
      ctaTitle: "Cible les bons prospects, au bon moment",
      ctaText:
        "On identifie les situations qui reviennent chez ceux qui ont signé, et on les traduit en signaux repérables chez d'autres. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La plupart des prospections partent d'un filtre : un titre de poste, un secteur, une taille d'entreprise. On construit une liste de gens qui rentrent dans ces cases, on les contacte tous avec le même message, puis on s'étonne que le taux de réponse soit famélique.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est ni ton message, ni ton canal. C'est ta cible. Tu vises un profil, alors que ce qui déclenche une réponse, c'est une situation.",
        },
        { type: ArticleBlockType.H2, text: "Un profil ne dit pas si la personne a besoin de toi" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Prends deux directeurs commerciaux. Même titre, même secteur, même taille d'entreprise. Sur le papier ils sont identiques, ils tombent dans le même filtre.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Mais l'un vient de doubler son équipe et galère à faire monter ses nouveaux commerciaux. L'autre a une équipe stable et rodée depuis trois ans. Si tu vends un outil de coaching commercial, le premier a un besoin brûlant. Le second n'en a aucun.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le profil te dit qui est la personne. La situation te dit si elle a besoin de toi, maintenant.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est toute la différence. Cibler un profil, c'est ratisser large en espérant tomber juste. Cibler une situation, c'est parler à des gens qui vivent précisément le problème que tu résous.",
        },
        { type: ArticleBlockType.H2, text: "Profil contre situation, concrètement" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le ciblage par profil",
            items: [
              "Titre de poste, secteur, taille d'entreprise",
              "Une liste figée de suspects",
              "Même message pour tout le monde",
              "Tu supposes le besoin",
              "Beaucoup de volume, peu de réponses",
            ],
          },
          good: {
            title: "Le ciblage par situation",
            items: [
              "Un contexte déclencheur, un moment",
              "Des gens qui vivent le problème maintenant",
              "Message ancré dans leur situation",
              "Tu réponds à un besoin réel",
              "Moins de volume, plus de réponses",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "C'est quoi une situation qui déclenche un besoin" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une situation, c'est un contexte qui crée ou révèle le besoin que tu adresses. Quelques exemples selon ce que tu vends :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Une entreprise qui **vient de lever des fonds** : elle va recruter, structurer, scaler.",
            "Une boîte qui **recrute plusieurs commerciaux** d'un coup : elle va devoir les former et les outiller.",
            "Un dirigeant qui **vient de changer de poste** : il veut faire ses preuves, il est ouvert au changement.",
            "Une équipe qui **change d'outil ou de CRM** : moment de bascule, tout est réinterrogé.",
            "Un fondateur qui **parle publiquement d'un problème** que tu résous : il est déjà conscient du besoin.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans chacun de ces cas, la personne n'est pas juste un titre de poste. Elle est dans un moment où ton offre tombe à point. C'est ça qui fait qu'elle répond.",
        },
        { type: ArticleBlockType.H2, text: "Comment cibler une situation en pratique" },
        { type: ArticleBlockType.H3, text: "1. Identifie les situations de tes meilleurs clients" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Regarde tes clients actuels, surtout ceux qui ont signé vite et sont contents. Dans quelle situation étaient-ils quand ils t'ont trouvé ? Un événement, un changement, une pression ? Ce sont ces situations que tu dois rechercher chez d'autres.",
        },
        { type: ArticleBlockType.H3, text: "2. Traduis la situation en signaux repérables" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une situation laisse des traces visibles : une levée annoncée, une offre d'emploi publiée, un changement de poste, un post LinkedIn. Liste les signaux concrets qui trahissent la situation que tu vises.",
        },
        { type: ArticleBlockType.H3, text: "3. Cherche les signaux, pas les profils" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Au lieu de filtrer une base sur des critères statiques, pars à la chasse aux signaux. Qui vient de lever ? Qui recrute des commerciaux ? Qui parle du problème que tu résous ? Ta liste devient une liste de gens en situation de besoin.",
        },
        {
          type: ArticleBlockType.H3,
          text: "4. Contacte en parlant de leur situation, pas de toi",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ton message ne commence plus par ce que tu vends. Il commence par leur situation, ce que tu as vu et qui justifie que tu écrives aujourd'hui. Tu montres que tu les as ciblés pour une raison précise, et c'est ce qui sépare un message ignoré d'un message qui obtient une réponse.",
        },
        { type: ArticleBlockType.H2, text: "Le lien avec ce que disent tes clients" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le meilleur endroit pour trouver les situations qui comptent, ce ne sont pas les bases de données. Ce sont tes propres conversations clients. Quand tu écoutes tes appels, tu entends des phrases comme *on venait de doubler l'équipe* ou *notre ancien outil nous lâchait*. Ce sont tes situations gagnantes, dictées par tes vrais clients.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On part de tes appels clients pour construire ton avatar réel, et on en tire les situations récurrentes de ceux qui achètent vraiment. Ces critères alimentent ensuite la recherche de prospects : l'extension scanne ton réseau et remonte les profils qui correspondent, non pas à un titre de poste, mais à la situation qui déclenche le besoin. Une donnée captée une fois, réutilisée pour cibler juste.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Arrête de viser des titres de poste. Un profil ne t'apprend rien sur le besoin réel de la personne. Repère plutôt les situations qui déclenchent ce besoin, traduis-les en signaux repérables, et contacte les gens en parlant de leur contexte. Tu enverras moins de messages, mais tu obtiendras beaucoup plus de réponses.",
        },
      ],
      faq: [
        {
          question: "C'est quoi cibler une situation plutôt qu'un profil ?",
          answer:
            "Cibler un profil, c'est viser un titre de poste, un secteur, une taille d'entreprise. Cibler une situation, c'est viser un contexte déclencheur : une entreprise qui vient de lever, qui recrute des commerciaux, qui change d'outil. La situation dit si la personne a besoin de toi maintenant, le profil non.",
        },
        {
          question: "Pourquoi le ciblage par profil ne suffit pas en prospection B2B ?",
          answer:
            "Parce qu'un titre de poste identique peut cacher des besoins très différents. Deux directeurs commerciaux au même poste n'ont pas le même problème si l'un vient de doubler son équipe et l'autre non. Le profil dit qui est la personne, pas ce qu'elle vit.",
        },
        {
          question: "Où trouver les signaux de situation pour cibler ?",
          answer:
            "Dans les signaux d'intention : levées de fonds, recrutements, changements de poste, publications, changements d'outils. Et surtout dans tes propres appels clients, qui révèlent les situations récurrentes de ceux qui achètent vraiment chez toi.",
        },
      ],
    },
    en: {
      title: "Target a situation, not a profile, to get replies",
      excerpt:
        "A job title does not tell you whether someone needs you today. Here is how to target the situation instead of the profile, and start getting replies.",
      description:
        "In B2B prospecting, a job title does not tell you whether someone needs you. Target the situation rather than the profile.",
      ctaTitle: "Reach the right prospects, at the right moment",
      ctaText:
        "We identify the situations that keep coming up among the customers who signed, and turn them into signals you can spot elsewhere. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most outreach starts from a filter: a job title, an industry, a company size. You build a list of people who fit those boxes, contact them all with the same message, then wonder why the reply rate is so thin.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is neither your message nor your channel. It is your target. You are aiming at a profile, when what triggers a reply is a situation.",
        },
        { type: ArticleBlockType.H2, text: "A profile does not tell you whether they need you" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Take two sales directors. Same title, same industry, same company size. On paper they are identical, they fall into the same filter.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "But one has just doubled the team and is struggling to ramp up the new reps. The other has had a stable, seasoned team for three years. If you sell a sales coaching tool, the first has a burning need. The second has none.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "The profile tells you who the person is. The situation tells you whether they need you, right now.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is the whole difference. Targeting a profile means casting wide and hoping to land. Targeting a situation means speaking to people who are living exactly the problem you solve.",
        },
        { type: ArticleBlockType.H2, text: "Profile versus situation, concretely" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Profile targeting",
            items: [
              "Job title, industry, company size",
              "A frozen list of suspects",
              "The same message for everyone",
              "You assume the need",
              "Plenty of volume, few replies",
            ],
          },
          good: {
            title: "Situation targeting",
            items: [
              "A trigger context, a moment",
              "People living the problem right now",
              "A message anchored in their situation",
              "You answer a real need",
              "Less volume, more replies",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "What counts as a situation that triggers a need" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A situation is a context that creates or reveals the need you address. A few examples, depending on what you sell:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "A company that **just raised funds**: it is about to hire, structure and scale.",
            "A business **hiring several sales reps** at once: it will have to train and equip them.",
            "A leader who **just changed roles**: they want to prove themselves and are open to change.",
            "A team **switching tools or CRM**: a turning point where everything is questioned again.",
            "A founder **publicly talking about a problem** you solve: they are already aware of the need.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In each of those cases the person is not just a job title. They are in a moment where your offer lands at the right time. That is what makes them reply.",
        },
        { type: ArticleBlockType.H2, text: "How to target a situation in practice" },
        { type: ArticleBlockType.H3, text: "1. Identify your best customers' situations" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Look at your current customers, especially the ones who signed quickly and are happy. What situation were they in when they found you? An event, a change, a pressure? Those are the situations you should be looking for elsewhere.",
        },
        { type: ArticleBlockType.H3, text: "2. Turn the situation into observable signals" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A situation leaves visible traces: an announced round, a published job ad, a role change, a LinkedIn post. List the concrete signals that give away the situation you are targeting.",
        },
        { type: ArticleBlockType.H3, text: "3. Hunt for signals, not for profiles" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Instead of filtering a database on static criteria, go hunting for signals. Who just raised? Who is hiring sales reps? Who is talking about the problem you solve? Your list becomes a list of people who are in a moment of need.",
        },
        { type: ArticleBlockType.H3, text: "4. Reach out about their situation, not about you" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Your message no longer opens on what you sell. It opens on their situation, on what you saw and why it makes today the day to write. You show you picked them for a precise reason, and that is what separates an ignored message from one that gets an answer.",
        },
        { type: ArticleBlockType.H2, text: "The link with what your customers say" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The best place to find the situations that matter is not a database. It is your own customer conversations. Listening back to your calls, you hear sentences like *we had just doubled the team* or *our old tool was falling apart*. Those are your winning situations, dictated by real customers.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We start from your customer calls to build your real avatar, and draw out the recurring situations of the people who actually buy. Those criteria then feed prospect search: the extension scans your network and surfaces the profiles that match, not a job title, but the situation that triggers the need. Captured once, reused to target accurately.",
          linkLabel: "Discover prospect search",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Stop aiming at job titles. A profile teaches you nothing about someone's real need. Spot the situations that trigger that need, turn them into observable signals, and reach out about their context. You will send fewer messages, and get far more replies.",
        },
      ],
      faq: [
        {
          question: "What does targeting a situation rather than a profile mean?",
          answer:
            "Targeting a profile means aiming at a job title, an industry, a company size. Targeting a situation means aiming at a trigger context: a company that just raised, that is hiring sales reps, that is switching tools. The situation tells you whether the person needs you now, the profile does not.",
        },
        {
          question: "Why is profile targeting not enough in B2B prospecting?",
          answer:
            "Because an identical job title can hide very different needs. Two sales directors in the same role do not have the same problem if one has just doubled the team and the other has not. The profile says who the person is, not what they are living through.",
        },
        {
          question: "Where do you find situation signals to target?",
          answer:
            "In intent signals: funding rounds, hiring, role changes, publications, tool changes. And above all in your own customer calls, which reveal the recurring situations of the people who actually buy from you.",
        },
      ],
    },
  },
};
